import { useCallback, useId, useState } from 'react';
import { LOCALES, useLocale, useT } from '../../i18n/index.js';
import { Glyph } from './Glyph.js';
import { useDismiss } from './useDismiss.js';

/**
 * The passenger's language, in the app chrome.
 *
 * It used to sit at the top of Help, on the reasoning that the operator's default is right
 * for most people most of the time and this is a correction rather than a question the app
 * should put to everyone. That reasoning still holds -- and it is exactly why the control
 * belongs here now. A correction is only useful where the mistake is noticed, which is
 * whatever screen the passenger is looking at, not the one page they would have to already
 * read English to go and find.
 *
 * Every option carries its own name in its own language, unchanged from the segmented
 * control this replaces. Somebody who cannot read the current language finds theirs by
 * recognising it, so translating "Kiswahili" into French would defeat the entire control.
 */

export type LocaleVariant = 'mark' | 'icon';

export function LocalePicker({ variant = 'mark' }: { variant?: LocaleVariant }) {
  const t = useT();
  const { locale, setLocale } = useLocale();
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const ref = useDismiss(open, close);
  const menuId = useId();

  const current = LOCALES.find((option) => option.code === locale);

  return (
    <div ref={ref} className="nav-account">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-controls={menuId}
        aria-label={t('c.language')}
        title={t('c.language')}
        className="nav-locale"
      >
        <Glyph name="globe" size={17} />
        {/* The code, not the endonym: "Français" in a phone-width header pushes the account
            mark off the end of the bar. The full name is one tap away, in the menu. */}
        {variant === 'mark' && <span className="nav-locale-code">{current?.short}</span>}
      </button>

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={t('c.language')}
          className={`nav-menu ${variant === 'icon' ? 'nav-menu-side' : 'nav-menu-below'}`}
        >
          {LOCALES.map((option) => {
            const active = option.code === locale;
            return (
              <button
                key={option.code}
                type="button"
                role="menuitemradio"
                aria-checked={active}
                onClick={() => {
                  setLocale(option.code);
                  close();
                }}
                className="nav-menu-item nav-locale-item"
                data-active={active}
              >
                <span className="nav-locale-short">{option.short}</span>
                {option.label}
                {active && <Glyph name="check" size={15} className="nav-locale-tick" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
